import { useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import logDeviceIcon from "../../oversight-core/assets/images/site-log-device.svg";
import { EVrmDeviceType } from "../../oversight-core/enums/vrm-device-type";
import { IExtendGenericSolarPlantInverters } from "../../oversight-core/interfaces/generic-page-solar-plant-inverter-view";
import VrmInverterInformation from "../../oversight-core/ui-elements/inverter-information/vrm-inverter-information";
import MaterialIcon from "../../oversight-core/ui-elements/material-icon/material-icon";
import { getRelativeTimeFromTimestamp } from "../../oversight-core/utils/time-utils";
import translate from "../../oversight-core/utils/translate";
import styles from "./site-device-log-item.module.scss";

interface IProps {
  inverter: IExtendGenericSolarPlantInverters;
}

const SiteDeviceLogItem = (props: IProps) => {
  const { inverter } = props;

  const [collapsed, setCollapsed] = useState(false);

  const getDeviceInfo = useMemo(() => {
    switch (inverter.device.deviceType) {
      case EVrmDeviceType.GATEWAY:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Firmware version",
                value: inverter.device.firmwareVersion,
              },
              {
                title: "Remote on LAN",
                value: inverter.device.remoteOnLan,
              },
              {
                title: "Firmware Updates",
                value: inverter.device.updateTo,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "Login Interval",
                value: inverter.device.loggingInterval,
              },
              {
                title: "VRM portal ID",
                value: inverter.device.identifier,
              },
              {
                title: "Last power up or restart",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastPowerUpOrRestart
                ),
              },
              {
                title: "Remote Console on VRM",
                value: inverter.device.vncStatus,
              },
              {
                title: "Two way communication",
                value: inverter.device.twoWayCommunication
                  ? "Enabled"
                  : "Disabled",
              },
              {
                title: "Remote support",
                value: `${translate(inverter.device.remoteSupport)} - ${
                  inverter.device.remoteSupportIp
                }:${inverter.device.remoteSupportPort}`,
              },
            ]}
          />
        );
      case EVrmDeviceType.BATTERY_MANAGEMENT_SYSTEM:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Firmware version",
                value: inverter.device.firmwareVersion,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "Product id",
                value: inverter.device.productId,
              },
              {
                title: "VE.Bus connection",
                value: inverter.device.vmc,
              },
              {
                title: "Hardware Configuration",
                value: inverter.device.vid.enumValue,
              },
              {
                title: "VRM instance",
                value: inverter.device.instance,
              },
            ]}
          />
        );
      case EVrmDeviceType.BATTERY_MONITOR:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Firmware Version",
                value: inverter.device.firmwareVersion,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "Battery Manufacturer",
                value: inverter.device.batteryManufacturer,
              },
              {
                title: "Battery Family",
                value: inverter.device.batteryFamily,
              },
              {
                title: "VRM instance",
                value: inverter.device.instance,
              },
            ]}
          />
        );
      case EVrmDeviceType.SOLAR_CHARGER:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Firmware Version",
                value: inverter.device.firmwareVersion,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "VRM instance",
                value: inverter.device.instance,
              },
            ]}
          />
        );
      case EVrmDeviceType.TANK_SENSOR:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Firmware Version",
                value: inverter.device.firmwareVersion,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "VRM instance",
                value: inverter.device.instance,
              },
            ]}
          />
        );
      case EVrmDeviceType.TEMPERATURE_SENSOR:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "VRM instance",
                value: inverter.device.instance,
              },
            ]}
          />
        );
      default:
        return (
          <VrmInverterInformation
            inverterInfo={[
              {
                title: "Product",
                value: inverter.device.productName,
              },
              {
                title: "Last Connection",
                value: getRelativeTimeFromTimestamp(
                  inverter.device.lastConnection
                ),
              },
              {
                title: "Fronius device type",
                value: inverter.device.froniusDeviceType,
              },
              {
                title: "Position",
                value: inverter.device.pL,
              },
              {
                title: "VRM instance",
                value: inverter.device.instance,
              },
            ]}
          />
        );
    }
  }, [inverter.device]);

  const getDeviceName = useMemo(() => {
    switch (inverter.device.deviceType) {
      case EVrmDeviceType.BATTERY_MANAGEMENT_SYSTEM:
        return inverter.device.name;

      case EVrmDeviceType.BATTERY_MONITOR:
        return (
          <>
            <Row>
              <Col>{inverter.device.customName}</Col>
            </Row>
            <Row className="text-light font-weight-400 font-size-12">
              <Col>{`${inverter.device.name}[${inverter.device.instance}]`}</Col>
            </Row>
          </>
        );

      default:
        return `${inverter.device.name} ${
          inverter.device.instance !== undefined
            ? `[${inverter.device.instance}]`
            : ""
        }`;
    }
  }, [inverter.device]);

  return (
    <div
      className={`${styles.deviceLogItemContainer} bg-white p-3 rounded-3 cursor-pointer`}
      onClick={() => setCollapsed((ps) => !ps)}
    >
      <Row className="align-items-center justify-content-between">
        <Col className="col-5 col-sm-7">
          <Row className="align-items-center">
            <Col className="col-auto pe-0">
              <img src={logDeviceIcon} alt="Device Icon" />
            </Col>
            <Col className="text-dark font-weight-500 font-size-14">
              {getDeviceName}
            </Col>
          </Row>
        </Col>
        <Col className="col-4 text-light font-weight-400 font-size-12">
          Last seen&nbsp;
          {getRelativeTimeFromTimestamp(inverter.device.lastConnection)}
        </Col>
        <Col className="col-auto">
          <MaterialIcon
            icon={collapsed ? "keyboard_arrow_down" : "keyboard_arrow_right"}
            color="#AEBBD0"
            size={25}
          />
        </Col>
      </Row>
      {collapsed && <div className="ms-4 ps-3 mt-4">{getDeviceInfo}</div>}
    </div>
  );
};

export default SiteDeviceLogItem;
